import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import CategoryTable from './child/CategoryTable';
import AddEditCategoryModal from './child/AddEditCategoryModal';
import { Icon } from '@iconify/react';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const CategoriesLayer = () => {
    const { auth } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });
    const [showModal, setShowModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    const fetchCategories = useCallback(async (page = 1, searchQuery = '', sort = sortConfig) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: 20,
                search: searchQuery,
                sort_by: sort.key,
                order: sort.direction
            });
            const response = await fetch(`${config.API_BASE_URL}/api/categories?${queryParams}`);
            const data = await response.json();
            
            if (response.ok) {
                setCategories(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.error || 'Failed to fetch categories');
            }
        } catch (error) {
            toast.error('Error connecting to server');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [sortConfig]);

    useEffect(() => {
        fetchCategories(pagination.page, search);
    }, [fetchCategories, pagination.page, search]);

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on search
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleAdd = () => {
        setEditingCategory(null);
        setShowModal(true);
    };

    const handleEdit = (category) => {
        setEditingCategory(category);
        setShowModal(true);
    };

    const handleDelete = async (category) => {
        const venueCount = Number(category.venue_count || 0);
        if (category.is_default) {
            toast.info('Default categories are protected and cannot be deleted.');
            return;
        }
        if (venueCount > 0) {
            toast.info(`Reassign ${venueCount} ${venueCount === 1 ? 'venue' : 'venues'} before deleting this category.`);
            return;
        }
        if (!window.confirm(`Delete "${category.name}"? This action cannot be undone.`)) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/categories/${category.id}`, {
                method: 'DELETE',
            });
            
            if (response.ok) {
                toast.success('Category deleted successfully');
                fetchCategories(pagination.page, search);
            } else {
                const data = await response.json();
                toast.error(data.message || data.error || 'Failed to delete category');
            }
        } catch (error) {
            toast.error('Error deleting category');
        }
    };

    const handleSave = async (formData) => {
        const url = editingCategory 
            ? `${config.API_BASE_URL}/api/categories/${editingCategory.id}`
            : `${config.API_BASE_URL}/api/categories`;
        
        const method = editingCategory ? 'PUT' : 'POST';

        // formData comes as FormData object from the modal now
        // But for backward compatibility or if modal passes object, handle both
        const body = formData instanceof FormData ? formData : JSON.stringify(formData);

        try {
            const response = await auth.fetch(url, {
                method,
                body,
            });
            
            const data = await response.json();

            if (response.ok) {
                toast.success(`Category ${editingCategory ? 'updated' : 'added'} successfully`);
                setShowModal(false);
                fetchCategories(pagination.page, search);
            } else {
                toast.error(data.message || data.error || 'Operation failed');
            }
        } catch (error) {
            toast.error('Error saving category');
        }
    };

    return (
        <div className="hs-card p-0">
            <div className="p-20 border-bottom d-flex align-items-center flex-wrap gap-3 justify-content-between bg-white radius-top-12">
                <div className="d-flex align-items-center gap-3">
                    <div>
                        <h6 className="text-lg fw-bold text-dark mb-0">Venue Categories</h6>
                        <span className="text-xs text-secondary-light">Manage standard and custom categories for venues</span>
                    </div>
                    <span className="hs-badge hs-badge-info">
                        Total: {pagination.total}
                    </span>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div className="position-relative" style={{ width: '260px' }}>
                        <Icon icon="solar:magnifer-linear" className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary-light" />
                        <input 
                            type="text" 
                            className="hs-input ps-5" 
                            name="search" 
                            placeholder="Search categories..." 
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <button 
                        onClick={handleAdd} 
                        className="hs-btn-primary d-flex align-items-center gap-2"
                    >
                        <Icon icon="solar:add-circle-bold" className="text-lg" />
                        <span>Add Category</span>
                    </button>
                </div>
            </div>
            
            <div className="p-24">
                <CategoryTable 
                    categories={categories} 
                    loading={loading} 
                    onEdit={handleEdit} 
                    onDelete={handleDelete}
                    onSort={handleSort}
                    sortConfig={sortConfig}
                />
                
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex align-items-center justify-content-between mt-24 pt-20 border-top flex-wrap gap-2">
                        <span className="text-xs text-secondary-light">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} categories
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            <button 
                                className="hs-icon-btn hs-btn-sm"
                                disabled={pagination.page === 1}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            >
                                <Icon icon="solar:alt-arrow-left-linear" />
                            </button>
                            <span className="text-xs fw-semibold px-2 text-dark">
                                Page {pagination.page} of {pagination.total_pages}
                            </span>
                            <button 
                                className="hs-icon-btn hs-btn-sm"
                                disabled={pagination.page === pagination.total_pages}
                                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            >
                                <Icon icon="solar:alt-arrow-right-linear" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showModal && (
                <AddEditCategoryModal 
                    show={showModal} 
                    onHide={() => setShowModal(false)} 
                    onSave={handleSave} 
                    category={editingCategory} 
                />
            )}
        </div>
    );
};

export default CategoriesLayer;
